/**
 * CounterpartyAddressesPanel Component
 * One panel for a tenant's or a vendor's email addresses (BR-98 / BR-112): the always-visible
 * Primary email block ("We write here first"), then the extra addresses recognized as belonging
 * to the same counterparty with Make primary (swaps in place, no confirmation) and Remove
 * (confirmed). The recognized list is hidden while empty. Rendered inside the edit-tenant
 * modal (UnitDetailPage) and the Edit Vendor modal (SettingsVendorsPage).
 */

import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  listCounterpartyAddresses,
  removeCounterpartyAddress,
  makeCounterpartyAddressPrimary,
  type CounterpartyKind,
  type RecognizedAddress,
} from '../lib/api';
import { formatTimestamp, deriveTimezone } from '../utils/date';
import { asIsoDateTimeString } from '@resida/shared';
import { ConfirmDialog } from './ConfirmDialog';
import { Button } from './ui/button';

export interface CounterpartyAddressesOwner {
  kind: CounterpartyKind;
  id: string;
  /** The owner's primary email as the parent knows it; the server's list is authoritative once loaded. */
  primaryEmail: string | null;
  /** Display name used in the primary block's helper copy. */
  name: string;
}

interface CounterpartyAddressesPanelProps {
  owner: CounterpartyAddressesOwner;
  /** IANA timezone for rendering timestamps, e.g. the owning unit's derived timezone. */
  timezone?: string;
  /** Called with the new primary address after Make primary succeeds, so the parent form can follow. */
  onPrimaryChanged?: (newPrimary: string) => void;
}

export function CounterpartyAddressesPanel({
  owner,
  timezone,
  onPrimaryChanged,
}: CounterpartyAddressesPanelProps): JSX.Element {
  const { kind, id } = owner;
  const resolvedTimezone = timezone ?? deriveTimezone('');
  const [primary, setPrimary] = useState<string | null>(owner.primaryEmail);
  const [addresses, setAddresses] = useState<RecognizedAddress[]>([]);
  // Load failures get a "Try again" retry affordance; action failures don't — the still-visible
  // buttons already let the landlord retry the action itself.
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [pendingRemoval, setPendingRemoval] = useState<RecognizedAddress | null>(null);

  const load = useCallback(async () => {
    try {
      setLoadError(null);
      const list = await listCounterpartyAddresses(kind, id);
      setPrimary(list.primary);
      setAddresses(list.addresses);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load email addresses');
    }
  }, [kind, id]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleMakePrimary = async (entry: RecognizedAddress): Promise<void> => {
    try {
      setBusyId(entry.id);
      setActionError(null);
      await makeCounterpartyAddressPrimary(kind, id, entry.id);
      await load();
      onPrimaryChanged?.(entry.address);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to change the primary email');
    } finally {
      setBusyId(null);
    }
  };

  const handleConfirmRemove = async (): Promise<void> => {
    if (!pendingRemoval) return;
    const entry = pendingRemoval;
    try {
      setBusyId(entry.id);
      setActionError(null);
      await removeCounterpartyAddress(kind, id, entry.id);
      setAddresses((prev) => prev.filter((a) => a.id !== entry.id));
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to remove email address');
    } finally {
      setPendingRemoval(null);
      setBusyId(null);
    }
  };

  return (
    <div
      data-testid={`${kind}-alternate-emails-panel`}
      className="bg-card rounded-lg p-4 border border-border mt-4 mb-4"
    >
      <div
        data-testid="counterparty-primary-email"
        className="p-3 mb-4 rounded-md border border-primary/30 bg-primary/5"
      >
        <p className="text-xs font-semibold text-muted-foreground">Primary email</p>
        <p
          data-testid="counterparty-primary-email-address"
          className="text-sm font-semibold text-foreground truncate"
        >
          {primary ?? 'No email on file'}
        </p>
        <p className="text-xs text-muted-foreground">
          We write here first. Mail from every address below is also filed under {owner.name}.
        </p>
      </div>

      {loadError && (
        <div
          role="alert"
          className="p-3 mb-3 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-sm flex items-center justify-between gap-3"
        >
          <span>{loadError}</span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void load()}
            data-testid="alternate-emails-retry-button"
            className="min-h-[44px]"
          >
            Try again
          </Button>
        </div>
      )}

      {actionError && (
        <p role="alert" className="text-sm text-destructive mb-3" data-testid="counterparty-address-error">
          {actionError}
        </p>
      )}

      {addresses.length > 0 && (
        <>
          <h3 className="text-sm font-semibold text-foreground mb-3">Also recognized email addresses</h3>
          <div className="space-y-2">
            {addresses.map((entry) => (
              <div
                key={entry.id}
                data-testid={`${kind}-alternate-email-row`}
                className="flex flex-wrap items-center gap-2 p-3 rounded-md border border-border bg-card"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{entry.address}</p>
                  <p className="text-xs text-muted-foreground">
                    Added{' '}
                    {formatTimestamp(asIsoDateTimeString(entry.createdAt), resolvedTimezone, {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  data-testid="make-primary-button"
                  aria-label={`Make ${entry.address} the primary email`}
                  onClick={() => void handleMakePrimary(entry)}
                  disabled={busyId === entry.id}
                  className="text-xs min-h-[44px]"
                >
                  Make primary
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  data-testid="remove-alternate-email-button"
                  aria-label={`Remove ${entry.address}`}
                  onClick={() => setPendingRemoval(entry)}
                  disabled={busyId === entry.id}
                  className="text-xs min-h-[44px] min-w-[44px] text-destructive border-destructive/30 hover:bg-destructive/5 hover:text-destructive"
                >
                  Remove
                </Button>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Portaled so its buttons never belong to an enclosing edit form (they'd submit it), and
          lifted above the Edit Vendor modal's z-[9999] overlay so the confirmation is reachable. */}
      {createPortal(
        <div className="relative z-[10000]">
        <ConfirmDialog
          isOpen={pendingRemoval !== null}
          onClose={() => setPendingRemoval(null)}
          onConfirm={() => void handleConfirmRemove()}
          title="Remove Alternate Email"
          message={
            pendingRemoval
              ? `Are you sure you want to remove "${pendingRemoval.address}"? Future mail from this address will no longer automatically match this ${kind}.`
              : ''
          }
          confirmText="Remove"
          isDestructive
          isLoading={busyId === pendingRemoval?.id}
        />
        </div>,
        document.body
      )}
    </div>
  );
}
